/**
 * "Load alignments" rows for LandXML layers the viewer has not loaded — the
 * ones switched OFF in the layer drawer (or every one, when the dialog was
 * opened from the Data Center, where no layer is mounted at all). Their
 * alignments never reached `alignmentsAtom`, so without this an alignment
 * picker reads "Register an alignment first…" over a project that has them.
 *
 * Shared by the station-volumes section and the clearance dialog; the caller
 * owns `useUnloadedAlignmentLayers()` so it can also decide whether its empty
 * message still applies.
 */
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import type { UnloadedAlignmentLayer } from '@/hooks/useUnloadedAlignmentLayers';

export interface UnloadedAlignmentsOfferProps {
  layers: readonly UnloadedAlignmentLayer[];
  onLoad: (layerId: string) => void;
  /** Prefix of the container (`${p}-unloaded-alignments`) and button
   *  (`${p}-load-alignments-${id}`) test ids. */
  testIdPrefix: string;
  className?: string;
}

export function UnloadedAlignmentsOffer({ layers, onLoad, testIdPrefix, className }: UnloadedAlignmentsOfferProps) {
  const { t } = useTranslation('project');
  if (layers.length === 0) return null;
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`} data-testid={`${testIdPrefix}-unloaded-alignments`}>
      <p className='text-xs text-slate-600 dark:text-stone-400'>{t('stationVolumes.dialog.loadAlignmentsHint')}</p>
      {layers.map((l) => (
        <div key={l.id} className='flex min-w-0 items-center gap-2'>
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='min-w-0 shrink-0'
            data-testid={`${testIdPrefix}-load-alignments-${l.id}`}
            disabled={l.state === 'loading'}
            onClick={() => onLoad(l.id)}>
            {l.state === 'loading'
              ? t('stationVolumes.dialog.loadAlignmentsLoading')
              : t('stationVolumes.dialog.loadAlignments')}
          </Button>
          <span className='min-w-0 flex-1 truncate text-xs text-slate-600 dark:text-stone-400'>{l.name}</span>
          {l.state === 'empty' && (
            <span className='shrink-0 text-xs text-slate-500 dark:text-stone-400'>
              {t('stationVolumes.dialog.loadAlignmentsEmpty')}
            </span>
          )}
          {l.state === 'failed' && (
            <span className='shrink-0 text-xs text-amber-700 dark:text-amber-400'>
              {t('stationVolumes.dialog.loadAlignmentsFailed')}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
